<template>
    <v-progress-circular v-if="loading" indeterminate color="primary"></v-progress-circular>
    <v-table v-else class="shares">
        <thead>
            <tr>
                <th>Account</th>
                <th>Role</th>
                <th>Created</th>
                <th></th>
            </tr>
        </thead>
        <tbody>
            <tr v-for="share of shares" :key="share.sharedToAccountId">
                <td>
                    <AccountButton :account-id="share.sharedToAccountId" size="small" />
                </td>
                <td>
                    <strong>
                        <v-tooltip :text="share.roleDescription">
                            <template v-slot:activator="{ props }">
                                <span v-bind="props">{{ share.roleTitle }}</span>
                            </template></v-tooltip
                        >
                    </strong>
                </td>
                <td>
                    <TimeAgo :date="share.startDate" :timezone="timezone" />
                </td>
                <td class="text-right">
                    <v-btn @click="confirmRemoveShare(share)" icon="mdi-delete" color="red-darken-1" size="small" :loading="removingShare" />
                </td>
            </tr>
            <tr v-if="!shares || shares.length === 0">
                <td colspan="4" class="text-center">
                    <p>Not Shared to Other Accounts</p>
                </td>
            </tr>
        </tbody>
    </v-table>

    <v-dialog v-model="showConfirmDeleteShareDialog" max-width="500px">
        <v-card>
            <v-card-title>Delete Share?</v-card-title>
            <v-card-text>This will remove all access to the shared vessel from Account #{{ shareToRemove?.sharedToAccountId }}.</v-card-text>
            <v-card-actions>
                <v-btn color="grey-darken-1" @click="showConfirmDeleteShareDialog = false">No, Cancel</v-btn>
                <v-btn color="red-darken-2" @click="removeVesselShare">Yes, Remove Share</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
import { useApi } from '@/api'
import { handleApiError } from '@/lib/utils'
import type { VesselShareV1 } from '@geckoal/gecko-api-client'
import { useTimezone } from '@geckoal/gecko-webapp-utils'
import { onMounted, ref } from 'vue'
import AccountButton from '../buttons/AccountButton.vue'
import TimeAgo from '../TimeAgo.vue'

const timezone = useTimezone()

const props = defineProps({
    accountId: {
        type: Number,
        required: true
    },
    vesselId: {
        type: Number,
        required: true
    }
})

const shares = ref<VesselShareV1[] | undefined>()
const loading = ref(false)
const showConfirmDeleteShareDialog = ref(false)
const shareToRemove = ref<VesselShareV1 | undefined>()
const removingShare = ref(false)

const fetchVesselShares = async () => {
    handleApiError(
        async () => {
            loading.value = true
            const result = await useApi().getVesselSharesV1({ vesselId: props.vesselId })
            shares.value = result.shares
        },
        async () => {
            loading.value = false
        }
    )
}

onMounted(() => {
    fetchVesselShares()
})

const confirmRemoveShare = (share: VesselShareV1) => {
    shareToRemove.value = share
    showConfirmDeleteShareDialog.value = true
}

const removeVesselShare = async () => {
    if (shareToRemove.value === undefined) return
    const { sharedToAccountId, vesselId } = shareToRemove.value

    await handleApiError(
        async () => {
            removingShare.value = true

            const result = await useApi().removeAccountVesselV1({
                accountId: sharedToAccountId,
                vesselId
            })

            if (result.deleted) {
                console.log('Deleted vessel share', result.deleted)
            } else {
                throw new Error('Failed to delete vessel share')
            }

            fetchVesselShares()
        },
        async () => {
            removingShare.value = false
            showConfirmDeleteShareDialog.value = false
            shareToRemove.value = undefined
        }
    )
}
</script>

<style lang="scss" scoped></style>
